/**
 * AddressSelectQuestion Component
 * Looks up addresses from a postcode and displays them in a dropdown
 * Falls back to manual entry if lookup fails
 */

import { useState, useEffect } from 'react'
import { lookupAddresses, Address } from '../../../services/addressLookup'

interface AddressSelectQuestionProps {
  questionId: string
  postcode: string
  value: string | object // Can be address ID (string) or full address object
  onChange: (value: object) => void // Always returns address object with metadata
  required?: boolean
  error?: string
  onManualEntryRequired?: () => void
}

const AddressSelectQuestion = ({
  questionId,
  postcode,
  onChange,
  required = false,
  error,
  onManualEntryRequired
}: AddressSelectQuestionProps) => {
  const [loading, setLoading] = useState(true)
  const [addresses, setAddresses] = useState<Address[]>([])
  const [lookupError, setLookupError] = useState<string | null>(null)
  const [selectedIndex, setSelectedIndex] = useState<string>('')

  useEffect(() => {
    const fetchAddresses = async () => {
      setLoading(true)
      setLookupError(null)

      const result = await lookupAddresses(postcode)

      if (result.success && result.addresses.length > 0) {
        setAddresses(result.addresses)
        setLookupError(null)
      } else {
        setLookupError(result.error || 'Failed to find addresses')
        setAddresses([])

        // Trigger manual entry if lookup fails
        if (onManualEntryRequired) {
          onManualEntryRequired()
        }
      }

      setLoading(false)
    }

    if (postcode) {
      fetchAddresses()
    }
  }, [postcode, onManualEntryRequired])

  const handleAddressSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const index = e.target.value
    setSelectedIndex(index)

    if (index === '') {
      return
    }

    const selectedAddress = addresses[parseInt(index)]

    if (selectedAddress) {
      // Return address object with metadata
      onChange({
        ...selectedAddress,
        addressEntryMethod: 'API-LOOKUP'
      })
    }
  }

  // If loading, show loading state
  if (loading) {
    return (
      <div className="w-full max-w-xl">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-green border-t-transparent mx-auto mb-4"></div>
            <p className="text-gray-600">Finding addresses for {postcode}...</p>
          </div>
        </div>
      </div>
    )
  }

  // If lookup failed, show error message
  // The parent component should switch to manual entry mode
  if (lookupError) {
    return (
      <div className="w-full max-w-xl">
        <div className="p-6 bg-yellow-50 border-2 border-yellow-200 rounded-lg">
          <div className="flex items-start gap-3">
            <svg className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div>
              <h3 className="font-semibold text-yellow-900 mb-1">
                We're having trouble finding your address at {postcode}
              </h3>
              <p className="text-sm text-yellow-800">
                {lookupError}. Please enter your address manually below.
              </p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Show address selection dropdown
  return (
    <div className="w-full max-w-xl">
      <div className="relative">
        <select
          id={questionId}
          name={questionId}
          value={selectedIndex}
          onChange={handleAddressSelect}
          required={required}
          autoFocus
          className={`
            w-full px-6 py-4 text-lg border-2 rounded-lg appearance-none cursor-pointer transition-all duration-200
            focus:outline-none focus:ring-2 focus:ring-primary-green focus:ring-opacity-50
            ${selectedIndex ? 'text-dark-plum' : 'text-gray-400'}
            ${
              error
                ? 'border-red-500 bg-red-50'
                : 'border-gray-300 bg-white focus:border-primary-green'
            }
          `}
          aria-invalid={!!error}
          aria-describedby={error ? `${questionId}-error` : undefined}
        >
          <option value="">Select your address...</option>
          {addresses.map((address, index) => (
            <option key={index} value={index}>
              {address.fullAddress}
            </option>
          ))}
        </select>

        {/* Custom dropdown arrow */}
        <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
          <svg
            className="w-6 h-6 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </div>

      {/* Postcode display */}
      <div className="mt-3 text-sm text-gray-600">
        <span className="font-medium">Postcode:</span> {postcode}
      </div>

      {/* Address count */}
      <div className="mt-2 text-sm text-gray-500">
        {addresses.length} {addresses.length === 1 ? 'address' : 'addresses'} found
      </div>

      {/* Error Message */}
      {error && (
        <div
          id={`${questionId}-error`}
          className="mt-2 text-sm text-red-600 flex items-center gap-1"
          role="alert"
        >
          <svg
            className="w-4 h-4"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          {error}
        </div>
      )}
    </div>
  )
}

export default AddressSelectQuestion
